import { Dropdown, Menu, Table as TableAntd, Flex } from 'antd';
import React, { useEffect, useState } from 'react';
import { FieldArray } from 'formik';
import { FaDownload, FaFilePdf, FaPlus } from 'react-icons/fa';
import { getItems } from '~/services/hooks/items';
import Button from '~/components/Button';
import EventsTableImport from './modals/items';
import { Table } from '~/components/Table';
import { pdfEvents } from '~/services/hooks/events';
import { useCheckAuth } from '~/components/Can/checkAuth';
import { formatPrice, formatWeight, parsePrice } from '~/Utils/format';
import Strong from '~/components/Strong';
import { AiOutlineImport } from 'react-icons/ai';
import { swoiColumns } from '~/tables/stockWriteOffItems';
import { itemsEventColumns } from '~/tables/items';
import errorHandler from '~/Utils/errorHandler';

export const ConsignedsModal = ({
  t,
  loading,
  setLoading,
  values,
  setFieldValue,
  errors,
  visible,
  isMobile,
  eventRevenues,
}) => {
  const authCheck = useCheckAuth();
  const [items, setItems] = useState([]);
  const [modalEventsTableVisible, setModalEventsTableVisible] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await getItems('', 'no-paginated');
      setItems(data);
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setLoading(false);
  };

  const handlePdf = async (event, type) => {
    setLoading(true);
    try {
      event.pdf_type = type;
      event.total_cost = totalConsigneds.totalCostPrice;
      event.total_price = totalConsigneds.consignedTotal;
      event.total_weight = totalItems.totalWeight;
      const response = await pdfEvents(event, 'issue-swoi');
      window.open(response.data, '_blank');
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setLoading(false);
  };

  const menu = (
    <Menu align="center">
      <Menu.Item key="real" onClick={() => handlePdf(values, 'real')}>
        {t('Real')}
      </Menu.Item>
      <Menu.Item key="cliente" onClick={() => handlePdf(values, 'client')}>
        {t('Cliente')}
      </Menu.Item>
    </Menu>
  );

  const totalItems = values?.consigneds?.reduce(
    (acc, c) => {
      acc.priceTotal += parsePrice(c.price_unit ?? 0) * parsePrice(c.amount ?? 0);
      acc.totalWeight = Number((acc.totalWeight + (parsePrice(c.weight_total, 3) ?? 0)).toFixed(3));

      return acc;
    },
    {
      priceTotal: 0,
      totalWeight: 0,
    }
  );

  const totalConsigneds = values?.stock_write_offs?.swoi?.reduce(
    (acc, swoi) => {
      acc.consignedTotal += swoi.total_consigned_price ?? 0;
      acc.totalCostPrice += swoi.total_cost_price ?? 0;

      return acc;
    },
    {
      consignedTotal: 0,
      totalCostPrice: 0,
    }
  );

  const recalcConsigned = (index, fieldChanged, value) => {
    const item = items?.find((i) => i?.id === values?.consigneds[index]?.item_id);
    if (!item) return;

    const amountPerPack = item?.amount_per_pack;
    const unitWeight = item?.weight;

    let amount = values?.consigneds[index]?.amount;
    let packs = values?.consigneds[index]?.packs;
    let weight_total = values?.consigneds[index]?.weight_total;

    if (fieldChanged === 'amount') {
      amount = value;

      if (amountPerPack) {
        packs = amount / amountPerPack;
        setFieldValue(`consigneds.${index}.packs`, packs);
      }

      if (unitWeight) {
        weight_total = amount * unitWeight;

        setFieldValue(`consigneds.${index}.weight_total`, parseFloat(weight_total).toFixed(3));
      }
    }

    if (fieldChanged === 'packs') {
      packs = value;

      if (amountPerPack) {
        amount = parsePrice(packs) * amountPerPack;
        setFieldValue(`consigneds.${index}.amount`, amount);
      }

      if (unitWeight) {
        weight_total = amount * unitWeight;

        setFieldValue(`consigneds.${index}.weight_total`, parseFloat(weight_total).toFixed(3));
      }
    }
  };

  const fluidPermissions = () => {
    if (eventRevenues) {
      return !eventRevenues;
    }
    return authCheck('@events/itemsEdit');
  };

  useEffect(() => {
    if (visible) {
      fetchItems();
    }
  }, [visible]);

  return (
    <>
      <Flex align="center" justify="end" gap="20px" style={{ marginBottom: 20 }}>
        {!values?.stock_write_offs?.swoi ? (
          <>
            <Button
              color="primary"
              icon={<AiOutlineImport />}
              onClick={() => setModalEventsTableVisible(true)}
              disabled={!fluidPermissions()}
              style={{ width: isMobile ? '48%' : '' }}
            >
              {t('Importar bebidas')}
            </Button>
            <Button
              onClick={() => {
                const updatedSwoi = values.consigneds.map((item) => ({
                  item_name: item.item_name,
                  item_id: item.item_id,
                  consigned_amount: item.amount,
                  amount_returned: 0,
                  amount: item.amount,
                  item_cost: item.item_cost,
                  price_unit: item.price_unit,
                }));

                setFieldValue('stock_write_offs', { action: 0, without_cost: 0, swoi: updatedSwoi });
              }}
              style={{ width: isMobile ? '48%' : '' }}
              icon={<FaDownload />}
              disabled={!fluidPermissions() || values.consigneds?.length < 1}
            >
              {t('Dar baixa em estoque')}
            </Button>
          </>
        ) : (
          <Dropdown overlay={menu} trigger={['click']}>
            <Button
              color="primary"
              icon={<FaFilePdf />}
              style={{ marginRight: 20, width: isMobile ? '48%' : '', marginBottom: 15 }}
            >
              {t('Emitir PDF')}
            </Button>
          </Dropdown>
        )}
      </Flex>
      {!values?.stock_write_offs ? (
        <FieldArray name="consigneds">
          {({ push, remove }) => (
            <>
              <Table
                dataSource={values?.consigneds?.map((consigned, index) => ({
                  ...consigned,
                  key: index,
                  remove,
                }))}
                columns={itemsEventColumns(items, errors, fluidPermissions, recalcConsigned)}
                pagination={false}
                size="small"
                rowClassName={() => 'editable-row'}
                summary={() => {
                  return (
                    <TableAntd.Summary.Row style={{ backgroundColor: '#d6d6d6' }}>
                      <TableAntd.Summary.Cell>
                        <Strong>{t('TOTAL')}</Strong>
                      </TableAntd.Summary.Cell>
                      <TableAntd.Summary.Cell />
                      <TableAntd.Summary.Cell />
                      <TableAntd.Summary.Cell>Kg {formatWeight(totalItems.totalWeight)}</TableAntd.Summary.Cell>
                      <TableAntd.Summary.Cell />
                      <TableAntd.Summary.Cell>{formatPrice(totalItems.priceTotal)}</TableAntd.Summary.Cell>
                      <TableAntd.Summary.Cell />
                    </TableAntd.Summary.Row>
                  );
                }}
              />
              <Flex width="100%" align="center" justify="center">
                <Button
                  type="primary"
                  title="Adicionar item"
                  onClick={() =>
                    push({
                      item_id: null,
                      amount: null,
                      packs: '',
                      price_unit: '',
                      price_total: '0.00',
                    })
                  }
                  style={{ marginTop: '16px', marginLeft: 16 }}
                  disabled={!fluidPermissions()}
                >
                  <FaPlus /> {t('Adicionar item')}
                </Button>
              </Flex>
            </>
          )}
        </FieldArray>
      ) : (
        <>
          <Table
            pagination={false}
            rowKey="id"
            loading={loading}
            dataSource={values.stock_write_offs.swoi || []}
            columns={swoiColumns(setFieldValue, isMobile)}
            bordered
            summary={() => {
              return (
                <TableAntd.Summary.Row style={{ backgroundColor: '#d6d6d6' }}>
                  <TableAntd.Summary.Cell>
                    <Strong>{t('TOTAL')}</Strong>
                  </TableAntd.Summary.Cell>
                  <TableAntd.Summary.Cell />
                  <TableAntd.Summary.Cell />
                  <TableAntd.Summary.Cell />
                  <TableAntd.Summary.Cell />
                  <TableAntd.Summary.Cell>{formatPrice(totalConsigneds.totalCostPrice)}</TableAntd.Summary.Cell>
                  <TableAntd.Summary.Cell />
                  <TableAntd.Summary.Cell>{formatPrice(totalConsigneds.consignedTotal)}</TableAntd.Summary.Cell>
                </TableAntd.Summary.Row>
              );
            }}
          />
        </>
      )}
      <EventsTableImport
        visible={modalEventsTableVisible}
        setFieldValue={setFieldValue}
        isMobile={isMobile}
        onClose={() => setModalEventsTableVisible(false)}
      />
    </>
  );
};
